/**
 * The one indicator for "a model is working on this" — every LLM wait in the
 * app uses it, and nothing else does. Ordinary reads (a profile row, today's
 * matches) keep the plain `common.loading` text: a sweep on a 50ms fetch is
 * noise, and the point of this component is that seeing it means Claude is
 * thinking.
 *
 * Generalized out of `OnboardingProgress`, which had the idea first and now
 * composes this: a marigold segment travelling a pine rail, indefinitely,
 * because none of these waits has an honest percentage to report and faking
 * one would be padding (constitution §I).
 *
 * `patience` is the second half of that honesty. A wait that looks identical at
 * second 2 and second 25 reads as a hang, so the long ones say why they are
 * long once they pass `PATIENCE_MS`.
 */

import { useEffect, useState } from 'react'
import { useI18n } from '../i18n'
import type { TranslationKey } from '../i18n/types'
import './Thinking.css'

/** How long before a wait admits it is a long one. Exported so a wait that
 * deliberately does not use `Thinking` (Dashboard's non-LLM 50-120s matches
 * read, PIVOT-136) still mirrors this number instead of inventing a second
 * one — CLAUDE.md's "carry a fact, never re-derive it" applied to a constant. */
export const PATIENCE_MS = 10_000

interface ThinkingProps {
  /**
   * Caption shown from the start. §9 asks every LLM wait for one, so this is
   * optional only for the one caller that already names the work in a
   * heading of its own (`OnboardingProgress`'s `h1` sits right above its
   * `Thinking`) — not, as this comment used to say, for "the bare rail (a
   * chat turn)". That carve-out covered the interview's per-turn wait
   * (`InterviewChat`) with no caption at all, silently overriding §9 rather
   * than amending it (PIVOT-143). It now passes one like every other caller.
   */
  label?: TranslationKey
  /** Replaces `label` once the wait passes `PATIENCE_MS`. */
  patience?: TranslationKey
  /** A whole step is waiting, not one turn inside a conversation. */
  wide?: boolean
}

export function Thinking({ label, patience, wide = false }: ThinkingProps) {
  const { t } = useI18n()
  const [waited, setWaited] = useState(false)

  useEffect(() => {
    if (!patience) return
    const timer = setTimeout(() => setWaited(true), PATIENCE_MS)
    return () => clearTimeout(timer)
  }, [patience])

  const caption = waited && patience ? patience : label

  return (
    <span
      className={wide ? 'thinking thinking--wide' : 'thinking'}
      role="status"
      aria-live="polite"
    >
      <span className="thinking-rail" aria-hidden="true" />
      {caption && <span className="thinking-caption">{t(caption)}</span>}
    </span>
  )
}
